import get from 'lodash/get'
import head from 'lodash/head'
import {call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects'

import history from '../../history'

import * as checkoutActions from '../../actions/checkout'
import * as dashboardActions from '../../actions/dashboard'
import * as eventActions from '../../actions/events'
import * as promoActions from '../../actions/promo'
import * as checkoutApi from '../../api/api'
import {BAD_REQUEST, isBadResponse, isGoodResponse} from '../../api/statusCodes'
import {GET_SUMMARY, PROCESS_CHECKOUT, checkoutApiError} from '../../checkoutErrorCodes'
import * as checkoutSelectors from '../../store/checkout'
import {CheckoutStepSlugs} from '../../store/enum'
import {currentOrderId} from '../../store/events'
import {worldpayExpirationDate, worldpayPaypageRegistrationId} from '../../store/worldpay'
import * as URLS from '../../urls'

export function* getCompleteCheckoutPayload() {
  const orderId = yield select(currentOrderId)
  const billingVersion = yield select(checkoutSelectors.billingVersion)
  const shippingVersion = yield select(checkoutSelectors.shippingVersion)
  const expDate = yield select(worldpayExpirationDate)
  const paypageRegistrationId = yield select(worldpayPaypageRegistrationId)
  const shouldApplyAccountBalance = yield select(checkoutSelectors.shouldApplyAccountBalance)

  return [
    orderId,
    {
      apply_account_balance: shouldApplyAccountBalance,
      expDate,
      billing_version: billingVersion,
      paypageRegistrationId,
      shipping_version: shippingVersion,
    },
  ]
}

export function* completeCheckoutWorker() {
  try {
    yield put(checkoutActions.completeCheckoutPending())

    const payload = yield call(getCompleteCheckoutPayload)
    const {json, status} = yield call(checkoutApi.completeCheckout, ...payload)

    if (status === BAD_REQUEST) {
      const firstError = head(json.errors)
      throw firstError
    }

    if (isBadResponse(status)) {
      throw checkoutApiError(PROCESS_CHECKOUT, status)
    }

    yield put(checkoutActions.completeCheckoutSuccess())
  } catch (e) {
    yield put(checkoutActions.completeCheckoutFailure(e))
  }
}

export function* completeCheckoutSaga() {
  yield takeLatest(checkoutActions.COMPLETE_CHECKOUT, completeCheckoutWorker)
}

export function* completeCheckoutSuccessWorker() {
  const orderId = yield select(currentOrderId)

  // navigate to confirmation page
  yield call(history.replace, `${URLS.ORDER}/${orderId}/confirmation`)
  yield put(eventActions.clearCurrentEvent())
}

export function* completeCheckoutSuccessSaga() {
  yield takeLatest(checkoutActions.COMPLETE_CHECKOUT_SUCCESS, completeCheckoutSuccessWorker)
}

/**
 * Fetches data from the order summary endpoint
 *
 * @param {Object} [action]
 * @param {Object} [action.payload]
 * @param {Boolean} [action.payload.applyAccountBalance] Adjust totals to include account balance
 */
export function* getOrderSummaryWorker({payload: {applyAccountBalance = false} = {}} = {}) {
  try {
    yield put(checkoutActions.getOrderSummaryPending())
    const orderId = yield select(currentOrderId)

    const response = yield call(checkoutApi.getOrderSummary, {orderId, applyAccountBalance})
    if (isGoodResponse(response.status)) {
      const responseData = get(response, 'json', {})
      yield put(checkoutActions.getOrderSummarySuccess(responseData))
    } else {
      throw checkoutApiError(GET_SUMMARY, response.status)
    }
  } catch (e) {
    yield put(checkoutActions.getOrderSummaryFailure(e))
  }
}

export function* getOrderSummarySaga() {
  yield takeEvery(
    [
      checkoutActions.GET_ORDER_SUMMARY,
      promoActions.APPLY_PROMO_CODE_SUCCESS,
      // if the promo fails to apply, it will delete any existing codes so we need to get new totals
      promoActions.APPLY_PROMO_CODE_FAILURE,
    ],
    getOrderSummaryWorker,
  )
}

export function* refreshOrderSummaryWorker() {
  // we want to wait until precheckout validation has completed and see if
  // the order is valid before attempting to fetch the order summary
  yield take(checkoutActions.ORDER_IS_VALID_PRECHECKOUT)

  const currentStep = yield select(checkoutSelectors.currentStep)
  if (currentStep === CheckoutStepSlugs.review) {
    yield* getOrderSummaryWorker()
  }
}

export function* refreshOrderSummarySaga() {
  yield takeEvery(dashboardActions.CLOSE_DASHBOARD, refreshOrderSummaryWorker)
}
